/* ============================================================
   Photo viewer: tap a photo to see it full screen  (Lamp Boy custom)
   ------------------------------------------------------------
   Applies to:
   - the main product photo gallery (.splide--product): only the
     photos for the colour that's selected (not the thumbnail strip).
     Desktop only - on mobile these photos already fill the screen
     width, so tapping them does nothing.
   - "Looks by you": Slideshow sections whose "Custom CSS Class"
     setting contains "product-slideshow"
   How it works:
   - Each photo gets an invisible full-size button, so it can be
     opened by tap, click or keyboard. Buttons are re-added whenever
     the gallery redraws (e.g. after choosing another colour).
   - The viewer shows the already-loaded photo straight away, then
     swaps in a sharper copy sized to the screen.
   - Close: X button, Esc, or tap the dark background.
     Browse: arrows, keyboard left/right, or swipe.
   - Dragging a gallery still just slides it; it doesn't open.
   Styles live in assets/photo-viewer.css.
   ============================================================ */
(function () {
  var GALLERIES = [
    { root: '.splide--product', item: 'product photo', title: 'Product photos', desktopOnly: true },
    { root: 'section.product-slideshow', item: 'customer photo', title: 'Customer photos' }
  ];
  // Same breakpoint as the theme's lg: / the CSS (mobile is below 1024px).
  var DESKTOP = window.matchMedia('(min-width: 1024px)');
  var ROOTS = GALLERIES.map(function (g) { return g.root; }).join(', ');
  var NOT_A_PHOTO = 'video, iframe, model-viewer, deferred-media, product-model';
  var MOVE_TOLERANCE = 10; // px a press can move and still count as a tap
  var SWIPE_DISTANCE = 40; // px of sideways movement that counts as a swipe

  var ICONS = {
    prev: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false"><path fill-rule="evenodd" clip-rule="evenodd" d="m7 12.0009 8.0012-8.0007 1.4142 1.4142-6.587 6.5866 6.5859 6.5868L15 20.002l-8-8.0011z"/></svg>',
    next: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false"><path fill-rule="evenodd" clip-rule="evenodd" d="m16.415 12.0011-8.0012 8.0007-1.4141-1.4143 6.587-6.5866-6.586-6.5868L8.415 4l8 8.0011z"/></svg>',
    close: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false"><path d="M12 13.414l5.293 5.293 1.414-1.414L13.414 12l5.293-5.293-1.414-1.414L12 10.586 6.707 5.293 5.293 6.707 10.586 12l-5.293 5.293 1.414 1.414L12 13.414z"/></svg>'
  };

  var dialog, stage, img, count, closeBtn;
  var photos = [];
  var index = 0;
  var returnFocus = null;
  var loadToken = 0;

  /* ---------- finding the photos ---------- */

  function galleryFor(root) {
    for (var i = 0; i < GALLERIES.length; i++) {
      if (root.matches(GALLERIES[i].root)) return GALLERIES[i];
    }
    return GALLERIES[0];
  }

  // The carousel holding the gallery's own slides (not a nested thumbnail strip).
  function carousel(root) {
    return root.matches('.splide') ? root : root.querySelector('.splide');
  }

  // The gallery's photos, in order: its own real slides (no loop clones, no
  // thumbnails) that are currently shown (other colours' photos are hidden),
  // contain a photo, and have no video or link of their own.
  function photoSlides(root) {
    var sp = carousel(root);
    var slides = [];
    if (sp) {
      var all = sp.querySelectorAll('.splide__slide');
      for (var i = 0; i < all.length; i++) {
        if (all[i].closest('.splide') === sp && !all[i].classList.contains('splide__slide--clone')) {
          slides.push(all[i]);
        }
      }
    }
    if (!slides.length) slides = [root]; // single photo, no carousel
    return slides.filter(function (s) {
      return s.getClientRects().length > 0 &&
        s.querySelector('img') &&
        !s.querySelector(NOT_A_PHOTO) &&
        !s.querySelector('a[href], button:not(.pv-open)');
    });
  }

  // The photo actually shown in a slide (handles separate mobile/desktop images).
  function visibleImg(scope) {
    var imgs = scope.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      if (imgs[i].getClientRects().length) return imgs[i];
    }
    return imgs[0] || null;
  }

  // From the photo's srcset, the smallest size that's still sharp at the size the
  // viewer will show it (the box below matches the padding in photo-viewer.css).
  function bigSrc(el) {
    var desktop = window.innerWidth >= 1024;
    var boxW = Math.min(window.innerWidth - (desktop ? 192 : 24), 1200);
    var boxH = window.innerHeight - (desktop ? 144 : 112);
    var ratio = el.naturalWidth && el.naturalHeight ? el.naturalWidth / el.naturalHeight : 1;
    var want = Math.min(boxW, boxH * ratio) * Math.min(window.devicePixelRatio || 1, 2);
    var best = null;
    var largest = null;
    (el.getAttribute('srcset') || '').split(',').forEach(function (part) {
      var bits = part.trim().split(/\s+/);
      var w = parseInt(bits[1], 10);
      if (!bits[0] || !w) return;
      if (!largest || w > largest.w) largest = { url: bits[0], w: w };
      if (w >= want && (!best || w < best.w)) best = { url: bits[0], w: w };
    });
    return (best || largest || { url: el.currentSrc || el.src }).url;
  }

  function button(cls, label, icon) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'pv__btn ' + cls;
    b.setAttribute('aria-label', label);
    b.innerHTML = icon;
    return b;
  }

  /* ---------- viewer ---------- */

  function build() {
    dialog = document.createElement('dialog');
    dialog.className = 'pv';

    stage = document.createElement('div');
    stage.className = 'pv__stage';
    img = document.createElement('img');
    img.className = 'pv__img';
    img.alt = '';
    img.decoding = 'async';
    img.draggable = false;
    stage.appendChild(img);

    closeBtn = button('pv__close', 'Close', ICONS.close);
    var prevBtn = button('pv__prev', 'Previous photo', ICONS.prev);
    var nextBtn = button('pv__next', 'Next photo', ICONS.next);

    count = document.createElement('p');
    count.className = 'pv__count';
    count.setAttribute('aria-live', 'polite');

    dialog.appendChild(stage);
    dialog.appendChild(closeBtn);
    dialog.appendChild(prevBtn);
    dialog.appendChild(nextBtn);
    dialog.appendChild(count);
    document.body.appendChild(dialog);

    closeBtn.addEventListener('click', close);
    prevBtn.addEventListener('click', function () { show(index - 1); });
    nextBtn.addEventListener('click', function () { show(index + 1); });

    dialog.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); show(index - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); show(index + 1); }
      else if (e.key === 'Escape' && typeof dialog.showModal !== 'function') { close(); }
    });

    // Swipe left/right to browse; a plain tap on the dark area closes.
    var start = null;
    var swiped = false;
    dialog.addEventListener('pointerdown', function (e) {
      start = { x: e.clientX, y: e.clientY };
      swiped = false;
    });
    dialog.addEventListener('pointerup', function (e) {
      if (!start || photos.length < 2) { start = null; return; }
      var dx = e.clientX - start.x;
      var dy = e.clientY - start.y;
      start = null;
      if (Math.abs(dx) > SWIPE_DISTANCE && Math.abs(dx) > Math.abs(dy)) {
        swiped = true;
        show(dx < 0 ? index + 1 : index - 1);
      }
    });
    dialog.addEventListener('click', function (e) {
      if (swiped) { swiped = false; return; }
      if (e.target === dialog || e.target === stage) close();
    });

    dialog.addEventListener('close', function () {
      document.documentElement.classList.remove('pv-lock');
      if (returnFocus && returnFocus.isConnected && typeof returnFocus.focus === 'function') {
        try { returnFocus.focus({ preventScroll: true }); } catch (err) { returnFocus.focus(); }
      }
      returnFocus = null;
    });
  }

  function show(i) {
    if (!photos.length) return;
    index = (i + photos.length) % photos.length;
    var el = photos[index];
    var token = ++loadToken;

    img.src = el.currentSrc || el.src; // already on the page: shows instantly
    img.alt = (el.alt || 'Photo') + ' (' + (index + 1) + ' of ' + photos.length + ')';
    count.textContent = (index + 1) + ' / ' + photos.length;

    var full = new Image();
    full.decoding = 'async';
    full.onload = function () {
      if (token === loadToken) img.src = full.src;
    };
    full.src = bigSrc(el);

    // Warm up the neighbours so browsing feels instant.
    if (photos.length > 1) {
      [index + 1, index - 1].forEach(function (n) {
        var warm = new Image();
        warm.src = bigSrc(photos[(n + photos.length) % photos.length]);
      });
    }
  }

  function open(root, slides, i, trigger, fromKeyboard) {
    if (!dialog) build();
    photos = slides.map(visibleImg).filter(Boolean);
    if (!photos.length) return;
    dialog.setAttribute('aria-label', galleryFor(root).title);
    dialog.classList.toggle('pv--single', photos.length < 2);
    returnFocus = trigger || null;
    document.documentElement.classList.add('pv-lock');
    show(i);
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    // Focus ring on the close button only for keyboard users.
    try { closeBtn.focus({ focusVisible: !!fromKeyboard }); } catch (err) { closeBtn.focus(); }
  }

  function close() {
    if (!dialog) return;
    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else if (dialog.hasAttribute('open')) {
      dialog.removeAttribute('open');
      dialog.dispatchEvent(new Event('close'));
    }
  }

  // Open the viewer at the photo containing `el`. Returns false if `el` isn't in a photo.
  function openFrom(el, fromKeyboard) {
    var root = el.closest(ROOTS);
    if (!root) return false;
    if (galleryFor(root).desktopOnly && !DESKTOP.matches) return false; // product photos: not on mobile
    var slides = photoSlides(root);
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].contains(el)) {
        open(root, slides, i, slides[i].querySelector('.pv-open'), fromKeyboard);
        return true;
      }
    }
    return false;
  }

  /* ---------- buttons on the photos ---------- */

  function prepare(root) {
    var g = galleryFor(root);
    var slides = photoSlides(root);
    slides.forEach(function (slide, i) {
      var label = 'View ' + g.item + ' ' + (i + 1) + ' of ' + slides.length;
      var b = slide.querySelector('.pv-open');
      if (b) {
        if (b.getAttribute('aria-label') !== label) b.setAttribute('aria-label', label);
        return;
      }
      var host = slide === root ? root : (slide.firstElementChild || slide);
      if (window.getComputedStyle(host).position === 'static') host.style.position = 'relative';
      b = document.createElement('button');
      b.type = 'button';
      b.className = 'pv-open';
      b.setAttribute('aria-label', label);
      // Enter/Space open it directly: the carousel swallows the next click after a
      // drag, which would otherwise stop keyboard users opening a photo.
      b.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        openFrom(b, true);
      });
      host.appendChild(b);
    });
  }

  function prepareAll() {
    var roots = document.querySelectorAll(ROOTS);
    for (var i = 0; i < roots.length; i++) {
      prepare(roots[i]);
      watch(roots[i]);
    }
  }

  // Galleries redraw their photos (e.g. choosing another colour), so re-add the
  // buttons after any change - at most once per frame.
  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      prepareAll();
    });
  }
  function watch(root) {
    var host = root.closest('[id^="shopify-section"]') || root.parentElement;
    if (!host || host.hasAttribute('data-pv-watched')) return;
    host.setAttribute('data-pv-watched', '');
    new MutationObserver(schedule).observe(host, { childList: true, subtree: true });
  }

  /* ---------- tap / click ---------- */

  // Remember where a press started, so drags/swipes on a gallery don't open the viewer.
  var press = null;
  document.addEventListener('pointerdown', function (e) {
    press = e.target.closest && e.target.closest(ROOTS) ? { x: e.clientX, y: e.clientY } : null;
  }, true);

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t.closest || !t.closest(ROOTS)) return;
    if (!t.closest('.pv-open') && t.closest('a, button, input, select, textarea, label')) return; // links, dots, arrows

    if (press && e.detail !== 0) {
      var moved = Math.abs(e.clientX - press.x) + Math.abs(e.clientY - press.y);
      if (moved > MOVE_TOLERANCE) return; // it was a drag
    }

    if (openFrom(t, e.detail === 0)) e.preventDefault();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', prepareAll);
  } else {
    prepareAll();
  }
  document.addEventListener('shopify:section:load', prepareAll);
})();
